import { useEffect, useId, useMemo, useRef } from 'react'
import {
  AgGridReact,
  useGridCellEditor,
  type CustomCellEditorProps,
} from 'ag-grid-react'
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ICellRendererParams,
  type IHeaderParams,
} from 'ag-grid-community'
import { LockKeyhole, Pencil } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import {
  formatPercent,
  formatUnits,
  formatWaveDate,
  validateWavePercent,
  weekLabel,
  type CalculatedWaveWeek,
  type WaveWeek,
} from './model'
import './wave-planning.css'

ModuleRegistry.registerModules([AllCommunityModule])

interface MetricRow {
  id: string
  label: string
  kind: 'percent' | 'units'
  editable?: boolean
  reference?: boolean
  description: string
  values: number[]
}

function PercentEditor(
  props: CustomCellEditorProps<MetricRow, number | string>
) {
  const input = useRef<HTMLInputElement>(null)
  const errorId = useId()
  const value =
    typeof props.value === 'number'
      ? String(Number(props.value.toFixed(2)))
      : String(props.value ?? '')
  const error = validateWavePercent(value)

  useEffect(() => {
    input.current?.focus()
    input.current?.select()
  }, [])

  useGridCellEditor({
    isCancelAfterEnd: () =>
      validateWavePercent(input.current?.value ?? value) !== null,
  })

  return (
    <div
      className="w-60 rounded-md border border-border bg-card p-3 shadow-md"
      onKeyDownCapture={event => {
        if (event.key !== 'Escape') return
        event.preventDefault()
        event.stopPropagation()
        props.api.stopEditing(true)
        if (props.node.rowIndex !== null)
          props.api.setFocusedCell(props.node.rowIndex, props.column)
      }}
    >
      <label
        className="mb-2 block text-xs font-medium"
        htmlFor={errorId + '-input'}
      >
        {props.colDef.headerName} · Planned Wave %
      </label>
      <div className="relative">
        <Input
          ref={input}
          id={errorId + '-input'}
          value={value}
          inputMode="decimal"
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          className="h-9 pr-7 text-right tabular-nums"
          onChange={event => props.onValueChange(event.target.value)}
        />
        <span className="pointer-events-none absolute right-3 top-2 text-sm text-muted-foreground">
          %
        </span>
      </div>
      {error && (
        <p id={errorId} role="alert" className="mt-2 text-xs text-destructive">
          {error}
        </p>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        Enter to apply · Esc to cancel
      </p>
    </div>
  )
}

function MetricLabel({ data }: ICellRendererParams<MetricRow>) {
  if (!data) return null
  return (
    <div
      className="wave-metric-content flex w-full items-center justify-between gap-2"
      title={data.description}
    >
      <span className="wave-metric-name truncate text-xs font-medium">
        {data.label}
      </span>
      {data.editable && (
        <span className="flex shrink-0 items-center gap-1 text-[10px] font-semibold text-primary">
          <Pencil size={11} />
          Editable
        </span>
      )}
    </div>
  )
}

function WeekHeader({
  week,
  setWeek,
}: IHeaderParams & { week: WaveWeek; setWeek: number }) {
  return (
    <div className="flex w-full flex-col items-end gap-0.5 text-xs">
      <span
        className={
          week.week === setWeek ? 'font-semibold text-primary' : 'font-semibold'
        }
      >
        {week.event}
        {week.week === setWeek && ' · SET'}
      </span>
      <span>{formatWaveDate(week.date)}</span>
      <span className="text-[10px] font-normal text-muted-foreground">
        {weekLabel(week.week)}
      </span>
    </div>
  )
}

interface Props {
  weeks: CalculatedWaveWeek[]
  timeline: WaveWeek[]
  year: number
  setWeek: number
  onChange: (index: number, value: string) => void
}

export function WavePlanningGrid({
  weeks,
  timeline,
  year,
  setWeek,
  onChange,
}: Props) {
  const narrow = useMediaQuery('(max-width: 767px)')
  const mobile = useMediaQuery('(max-width: 639px)')
  const gridRef = useRef<AgGridReact<MetricRow>>(null)
  const rows = useMemo<MetricRow[]>(() => {
    const yy = String(year).slice(-2)
    const metric = (
      id: keyof CalculatedWaveWeek,
      label: string,
      kind: MetricRow['kind'],
      description: string,
      extras = {}
    ): MetricRow => ({
      id,
      label,
      kind,
      description,
      ...extras,
      values: weeks.map(
        week => Number(week[id]) * (kind === 'percent' ? 100 : 1)
      ),
    })
    return [
      metric(
        'historicalWave',
        `${yy === '26' ? '25' : '26'} Actual Wave % Ordered`,
        'percent',
        'Historical wave distribution. Read-only reference.',
        { reference: true }
      ),
      metric(
        'cumulativeDemandPercent',
        `${yy} Cum D/T %`,
        'percent',
        'Cumulative demand divided by planned purchase units.'
      ),
      metric(
        'plannedWave',
        `${yy} Planned Wave %`,
        'percent',
        'Enter the share of purchase units allocated to each wave.',
        { editable: true }
      ),
      metric(
        'cumulativeWave',
        `${yy} Cum Planned Wave %`,
        'percent',
        'Sum of planned waves through this week.'
      ),
      metric(
        'averageReceipts',
        `${yy} Avg Store Unit Receipts`,
        'units',
        'Weekly planned receipts divided by store count.'
      ),
      metric(
        'receipts',
        `${yy} Planned Unit Receipts`,
        'units',
        'Planned wave percentage multiplied by purchase units.'
      ),
      metric(
        'inventory',
        `${yy} Planned Units EOW OH`,
        'units',
        'Previous inventory plus receipts minus weekly demand.'
      ),
      metric(
        'averageInventory',
        `${yy} Avg Store Units EOW OH`,
        'units',
        'End-of-week inventory divided by store count.'
      ),
      metric(
        'receiptsInStores',
        'Planned Receipts in Stores %',
        'percent',
        'Prior cumulative waves plus half of the current wave.'
      ),
      metric(
        'salesInStores',
        'Planned Sales in Stores %',
        'percent',
        'Cumulative demand divided by total period demand.'
      ),
      metric(
        'variance',
        'Variance · Receipts − Sales',
        'percent',
        'Receipts percentage minus sales percentage for the same week.'
      ),
    ]
  }, [weeks, year])

  const columns = useMemo<ColDef<MetricRow>[]>(
    () => [
      {
        colId: 'metric',
        field: 'label',
        headerName: `${year} Plan`,
        pinned: 'left',
        lockPinned: true,
        width: mobile ? 145 : narrow ? 220 : 285,
        cellRenderer: MetricLabel,
        tooltipValueGetter: params => params.data?.description,
        cellClass: params =>
          params.data?.editable ? 'wave-input-label' : 'wave-metric-label',
      },
      ...timeline.map(
        (week, index): ColDef<MetricRow> => ({
          colId: weekLabel(week.week),
          headerName: weekLabel(week.week),
          width: 110,
          headerComponent: WeekHeader,
          headerComponentParams: { week, setWeek },
          headerClass: week.week === setWeek ? 'wave-set-header' : undefined,
          cellDataType: false,
          valueGetter: params => params.data?.values[index],
          valueFormatter: params =>
            params.data?.kind === 'percent'
              ? formatPercent(
                  Number(params.value) / 100,
                  params.data.id === 'cumulativeDemandPercent' ? 1 : 2
                )
              : formatUnits(Number(params.value)),
          editable: params =>
            !!params.data?.editable && week.event !== 'NO WAVE',
          cellEditor: PercentEditor,
          cellEditorPopup: true,
          cellEditorPopupPosition: 'under',
          suppressKeyboardEvent: params => {
            const target = params.event.target
            // AG Grid handles native key events before React's bubbling handlers.
            // Keep an invalid editor open on Enter/Tab; Escape still cancels normally.
            if (
              params.editing &&
              (params.event.key === 'Enter' || params.event.key === 'Tab') &&
              target instanceof HTMLInputElement &&
              validateWavePercent(target.value)
            ) {
              params.event.preventDefault()
              return true
            }
            return false
          },
          cellClass: params =>
            [
              'ag-right-aligned-cell',
              'wave-number',
              params.data?.editable
                ? week.event === 'NO WAVE'
                  ? 'wave-blocked-cell'
                  : 'wave-input-cell'
                : '',
              params.data?.reference ? 'wave-reference-cell' : '',
              (params.data?.id === 'inventory' ||
                params.data?.id === 'variance') &&
              Number(params.value) < -0.000001
                ? 'wave-negative-cell'
                : '',
            ].filter(Boolean),
          tooltipValueGetter: params =>
            week.event === 'NO WAVE'
              ? 'No wave is scheduled for this week.'
              : params.data?.description,
        })
      ),
    ],
    [timeline, year, setWeek, narrow, mobile]
  )

  return (
    <>
      <div
        className="wave-planning-grid ag-theme-michaels h-[550px] w-full"
        aria-label="Weekly wave planning"
      >
        <AgGridReact<MetricRow>
          ref={gridRef}
          theme="legacy"
          rowData={rows}
          columnDefs={columns}
          getRowId={params => params.data.id}
          defaultColDef={{
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
          }}
          rowHeight={mobile ? 58 : 42}
          headerHeight={70}
          singleClickEdit
          readOnlyEdit
          invalidEditValueMode="revert"
          stopEditingWhenCellsLoseFocus
          ensureDomOrder
          tooltipShowDelay={450}
          onCellEditRequest={event => {
            const index = weeks.findIndex(
              week => weekLabel(week.week) === event.column.getColId()
            )
            if (index >= 0) onChange(index, String(event.newValue ?? ''))
          }}
          onGridReady={event =>
            event.api.ensureColumnVisible(
              weekLabel(Math.max(5, setWeek - (mobile ? 0 : 1))),
              'start'
            )
          }
          tabToNextCell={params => {
            if (!params.editing) return params.nextCellPosition ?? false
            const current = weeks.findIndex(
              week =>
                weekLabel(week.week) ===
                params.previousCellPosition.column.getColId()
            )
            const direction = params.backwards ? -1 : 1
            let next = current + direction
            while (weeks[next]?.event === 'NO WAVE') next += direction
            const column =
              weeks[next] && params.api.getColumn(weekLabel(weeks[next].week))
            return column
              ? {
                  rowIndex: params.previousCellPosition.rowIndex,
                  rowPinned: null,
                  column,
                }
              : false
          }}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-secondary/40 px-4 py-2.5 text-xs text-muted-foreground">
        <span>41 weeks · Scroll horizontally to explore the season</span>
        <span className="inline-flex items-center gap-1.5">
          <LockKeyhole size={12} />
          Other rows are calculated or read-only references
        </span>
      </div>
    </>
  )
}
